// Imports
import { Tab, Dropdown, Collapse, Modal, Ripple, initTWE } from "tw-elements";
import Swiper from 'swiper';
import { Autoplay, Navigation, Pagination, EffectFade, Thumbs } from 'swiper/modules';
import Flickity from "flickity";
import { Fancybox } from "@fancyapps/ui";

// Initialize tw-elements components
initTWE({ Tab, Dropdown, Collapse, Modal, Ripple });

// Configure Swiper to use specific modules
Swiper.use([Navigation, Pagination, Autoplay, EffectFade, Thumbs]);

// Bind fancybox to elements
Fancybox.bind("[data-fancybox]", {});

// Page banner swiper initialization
const initPageBannerSwiper = () => {
    const homeBannerSwiper = document.querySelector('#main-hero .swiper');
    if (homeBannerSwiper) {
        const effect = homeBannerSwiper.getAttribute('data-effect') || 'slide';
        new Swiper(homeBannerSwiper, {
            effect,
            loop: true,
            speed: 1000,
            autoplay: { delay: 5000 },
            pagination: {
                el: homeBannerSwiper.querySelector('.swiper-pagination'),
                clickable: true,
            },
        });
    }
};

// Carousel sliders initialization
const initCarouselSliders = () => {
    document.querySelectorAll('.swiper-carousel .swiper').forEach(slider => {
        const settings = {
            initialSlide: parseInt(slider.getAttribute('data-initial-slide')) || 0,
            loop: slider.getAttribute('data-loop') === 'true',
            slidesPerView: parseInt(slider.getAttribute('data-min-slides')) || 1,
            spaceBetween: 10,
            speed: 1000,
            autoplay: { delay: 5000 },
            pagination: {
                el: slider.parentElement.querySelector('.swiper-pagination'),
                clickable: true,
                renderBullet: (index, className) => `<span class="${className}"><span>${index + 1}</span></span>`,
            },
            navigation: {
                nextEl: slider.parentElement.querySelector('.swiper-button-next'),
                prevEl: slider.parentElement.querySelector('.swiper-button-prev'),
            },
            breakpoints: {
                640: { slidesPerView: slider.getAttribute('data-min-slides') || 1, spaceBetween: 10 },
                768: { slidesPerView: slider.getAttribute('data-max-slides') || 2, spaceBetween: 20 },
                1024: { slidesPerView: slider.getAttribute('data-max-slides') || 3, spaceBetween: 30 },
            },
        };

        const carouselSwiper = new Swiper(slider, settings);
        attachProgressBarHandlers(slider, carouselSwiper);
    });
};

// Attach progress bar handlers to a slider
const attachProgressBarHandlers = (slider, swiperInstance) => {
    if (slider.classList.contains('has-progress-bar')) {
        let progressBar = slider.parentElement.querySelector('.swiper-progress-bar');
        let fraction = slider.parentElement.querySelector('.swiper-fraction');

        const updateProgress = () => {
            let progress = (swiperInstance.realIndex + 1) / swiperInstance.slides.length;
            progressBar.style.width = `${progress * 100}%`;
            fraction.querySelector('.swiper-current-slide').textContent = swiperInstance.realIndex + 1;
            fraction.querySelector('.swiper-total-slides').textContent = swiperInstance.slides.length;
        };

        swiperInstance.on('init', updateProgress);
        swiperInstance.on('slideChange', updateProgress);
    }
};

// Fixed header functionality
const fixedHeader = () => {
    const headers = document.querySelectorAll('#main-header.fixed-header, #mobile-header.fixed-header');
    let lastScrollY = window.scrollY;

    const updateHeaderClass = () => {
        if (!headers.length) return;

        const currentScrollY = window.scrollY;

        headers.forEach(header => {
            header.classList.remove('up-header-scrolled', 'down-header-scrolled', 'top-header');

            if (currentScrollY === 0) {
                header.classList.add('top-header');
            } else if (currentScrollY > lastScrollY && currentScrollY > 50) {
                header.classList.add('down-header-scrolled');
            } else if (currentScrollY < lastScrollY) {
                header.classList.add('up-header-scrolled');
            }
        });

        lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', updateHeaderClass);
    updateHeaderClass(); // Call on page load
};

// Dropdown toggles
const dropdownToggles = () => {
    document.querySelectorAll('[data-twe-dropdown-toggle-ref]').forEach(dropdownToggleEl => {
        const dropdown = new Dropdown(dropdownToggleEl, {
            display: 'static',
            dropdownAnimation: 'off',
        });

        dropdownToggleEl.addEventListener('show.twe.dropdown', () => dropdownToggleEl.parentElement.classList.add('show'));
        dropdownToggleEl.addEventListener('hide.twe.dropdown', () => dropdownToggleEl.parentElement.classList.remove('show'));
    });
};

// Product gallery and modal setup
const productGallerySetup = () => {
    const productImages = document.querySelector('.single-cha_product .images .main-images .swiper');
    const productThumbs = document.querySelector('.single-cha_product .images .thumbnails .swiper');

    if (productImages && productThumbs) {
        const initSwiper = () => {
            const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
            new Swiper(productImages, {
                loop: true,
                speed: 1000,
                thumbs: {
                    swiper: new Swiper(productThumbs, {
                        direction: isDesktop ? 'vertical' : 'horizontal',
                        spaceBetween: 10,
                        slidesPerView: 4,
                        freeMode: true,
                        watchSlidesVisibility: true,
                        watchSlidesProgress: true,
                        breakpoints: {
                            640: { slidesPerView: 3 },
                            768: { slidesPerView: 4 },
                        },
                    }),
                },
            });
        };

        initSwiper();
        window.addEventListener('resize', initSwiper);
    }
};

const scrollToTop = () => {
    const scrollToTopButton = document.querySelector('.scroll-to-top');
    if (scrollToTopButton) {
        scrollToTopButton.addEventListener('click', () => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    }
};

// Toggle Search Form
const toggleSearchForm = () => {
    const searchFormToggle = document.querySelectorAll('.search-toggle-btn');

    if (!searchFormToggle) return;

    searchFormToggle.forEach(toggle => {
        toggle.addEventListener('click', (e) => {
            e.preventDefault();
            let searchForm = toggle.closest('.header').querySelector('.search-form');
            searchForm.classList.toggle('hidden');
            searchForm.querySelector('input').focus();
        });
    });

    document.addEventListener('click', (e) => {
        if (!e.target.closest('.search-form') && !e.target.closest('.search-toggle-btn')) {
            document.querySelectorAll('.search-form').forEach(form => form.classList.add('hidden'));
        }
    });
};

// Toggle Mobile Menu
const toggleMobileMenu = () => {
    const mobileMenuButton = document.querySelector('.menu-toggle-btn');
    const mobileMenu = document.querySelector('#mobile-menu');
    const body = document.querySelector('body');

    if (!mobileMenuButton || !mobileMenu) return;

    const toggleMenu = () => {
        mobileMenu.classList.toggle('show');
        body.classList.toggle('menu-open');
    };

    const closeMenu = () => {
        mobileMenu.classList.remove('show');
        body.classList.remove('menu-open');
    };

    mobileMenuButton.addEventListener('click', toggleMenu);

    document.addEventListener('click', (e) => {
        if (!mobileMenu.contains(e.target) && !mobileMenuButton.contains(e.target)) {
            closeMenu();
        }
    });
};

// Initialize all components
document.addEventListener('DOMContentLoaded', () => {
    toggleMobileMenu();
    toggleSearchForm();
    initPageBannerSwiper();
    initCarouselSliders();
    fixedHeader();
    dropdownToggles();
    productGallerySetup();
    //singleProductImagesModal();
    scrollToTop();

    // Update menu items with children: append toggle button with SVG icon and attach toggle event
    const menuItemsWithChildren = document.querySelectorAll('#mobile-menu .menu-item-has-children');

    menuItemsWithChildren.forEach(item => {
        const link = item.querySelector('a');
        const subMenu = item.querySelector('.sub-menu');
        
        // Create toggle button with SVG icon
        const toggleButton = document.createElement('button');
        toggleButton.className = 'submenu-toggle-btn';
        toggleButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon">
      <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
    </svg>`;
        
        // Append toggle button after the link element
        link.parentNode.insertBefore(toggleButton, link.nextSibling);
        
        // Remove previous click event from link if exists (optional)
        // link.removeEventListener('click', ...);
        
        // Attach click event to the toggle button
        toggleButton.addEventListener('click', (e) => {
            e.preventDefault();
            item.classList.toggle('menu-open');
            if (subMenu) {
                subMenu.style.display = (subMenu.style.display === 'block') ? 'none' : 'block';
            }
        });
    });
});
